import { IntegrationsPortErrors } from 'modules/Integrations/domain'
import { getCachedIntegrations } from 'modules/Integrations/utils/getCachedIntegrations'
import { XeroIntegrationConfig } from 'modules/Integrations/integrationsConfig/xero/xero.integration.config'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { fetchQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { MINUTES } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { isLiveCustomerPushToggle } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'
import type { PaymentBankAccountsByCurrency } from 'Integrations/integrationsConfig/common/PaymentBankAccounts/paymentBankAccounts.types'

export const xeroConfigurationLoader = async (): Promise<{
  data: XeroIntegrationConfig | null
  error: IntegrationsPortErrors | null
}> => {
  let integrationsResponse
  try {
    integrationsResponse = await getCachedIntegrations()
  } catch {
    return {
      data: null,
      error: IntegrationsPortErrors.Other
    }
  }

  const xeroIntegration = integrationsResponse?.items.find(
    int => int.service === 'Xero'
  )

  if (xeroIntegration?.state !== 'CONNECTED') {
    return {
      data: {
        defaultCreditLedgerAccount: '',
        defaultLedgerAccount: '',
        defaultMinimumLedgerAccount: '',
        defaultTaxLiabilityLedgerAccount: '',
        untaxedCustomerTaxCode: '',
        importCustomerWorkflow: false,
        autoLinkCustomers: false,
        autoLinkSyncPermission: undefined,
        paymentBankAccounts: {},
        initialPaymentBankAccounts: {}
      },
      error: null
    }
  }

  const [togglesResponse, fullIntegration] = await Promise.all([
    dashboardv99990101Client.getEntityTypeSyncToggles(),
    fetchQuery(apiQueryClient, dashboard20240730Client.getIntegration, 'Xero', {
      staleTime: 1 * MINUTES
    }).catch(() => null)
  ])
  const autoLinkToggle =
    togglesResponse.data?.items.find(
      toggle =>
        toggle.integrationService === 'Xero' && isLiveCustomerPushToggle(toggle)
    ) ?? null
  const autoLinkCustomers = autoLinkToggle?.enabled ?? false
  const autoLinkSyncPermission = autoLinkToggle?.syncPermission

  const settings =
    fullIntegration?.service === 'Xero' ? fullIntegration.settings : null
  const paymentBankAccounts = (settings?.paymentBankAccounts ?? []).reduce(
    (accounts, { currency, bankAccountId }) =>
      bankAccountId ? { ...accounts, [currency]: bankAccountId } : accounts,
    {} as PaymentBankAccountsByCurrency
  )

  return {
    data: {
      defaultCreditLedgerAccount:
        xeroIntegration.defaultLedgerAccounts.creditAccount ?? '',
      defaultLedgerAccount:
        xeroIntegration.defaultLedgerAccounts.chargeAccount ?? '',
      defaultMinimumLedgerAccount:
        xeroIntegration.defaultLedgerAccounts.minimumAccount ?? '',
      defaultTaxLiabilityLedgerAccount:
        xeroIntegration.defaultLedgerAccounts.taxLiabilityAccount ?? '',
      importCustomerWorkflow: xeroIntegration?.enabled ?? false,
      untaxedCustomerTaxCode:
        xeroIntegration.defaultTaxCodes.untaxedCustomerTaxCode ?? '',
      autoLinkCustomers,
      autoLinkSyncPermission,
      paymentBankAccounts,
      initialPaymentBankAccounts: { ...paymentBankAccounts }
    },
    error: null
  }
}
